import React, { useCallback, useEffect, useState } from 'react'
import { BannerProps, UserProvider } from 'shared/components/Context/UserContext'
import { parseError } from 'shared/helpers/errors'
import { loadGoogleMapApis } from 'shared/helpers/loadGoogleMapApis'
import { toCamelCase } from 'shared/helpers/utils'
import { User as SharedUserModel } from 'shared/models/user'
import styled from 'styled-components'

import { getLoggedInUserIdentityWithCache } from '../../api/identity'
import { useErrorMessage } from '../../hooks/useErrorMessage'
import { useSuccessMessage } from '../../hooks/useSuccessMessage'
import { useWarningMessage } from '../../hooks/useWarningMessage'
import { User } from '../../models/user'
import { hasPermission, Permissions } from '../../Permissions'
import { APP_LAYOUT_CLASS_NAME } from '../AppLayout/AppLayout'

interface ConsoleUserProviderProps {
  children: React.ReactNode
  isPremium: boolean
  isFlexStarter: boolean
  hasStarterFeature: boolean
  hasFactoringFeature: boolean
  permissions: any
}

export function ConsoleUserProvider({
  children,
  isPremium,
  isFlexStarter,
  hasStarterFeature,
  hasFactoringFeature,
  permissions,
}: ConsoleUserProviderProps) {
  const canViewRates = true // Console user can view always rates
  const emailCanBeChanged = true // Console user can change email for anybody. TODO: make sure backend accept this
  const canChangeCustomUser = hasPermission(permissions || [], Permissions.ChangeCustomUser)
  const canViewReferrals = hasPermission(permissions || [], Permissions.ViewReferral)
  const successMessage = useSuccessMessage()
  const warningMessage = useWarningMessage()
  const errorMessage = useErrorMessage()
  const [user, setUser] = useState<User | null>(null)

  useEffect(() => {
    async function fetchUser() {
      const { user, web_google_api_key } = await getLoggedInUserIdentityWithCache()
      setUser(user)
      loadGoogleMapApis(web_google_api_key, () => null)
    }

    fetchUser()
  }, [])

  const showBanner = useCallback(
    (type: 'error' | 'success' | 'warning', data: BannerProps) => {
      if (type === 'error') {
        if (data.message) return errorMessage(data)
        return errorMessage({ message: parseError(data) })
      }
      if (type === 'warning') return warningMessage(data)
      successMessage(data)
    },
    [successMessage, warningMessage, errorMessage]
  )

  return (
    <UserProvider
      loggedInUser={
        toCamelCase({
          ...user,
          is_team_admin: user?.is_team_admin || user?.is_superuser || user?.is_staff,
        }) as SharedUserModel
      }
      isSidebarVisible={false}
      showBanner={showBanner}
      clearUserCache={() => Promise.resolve()}
      permissions={{ canViewRates, emailCanBeChanged, canChangeCustomUser, canViewReferrals }}
      subscription={{ isPremium, isFlexStarter, hasStarterFeature, hasFactoringFeature }}
    >
      <Wrap>{children}</Wrap>
    </UserProvider>
  )
}

const Wrap = styled.div`
  .${APP_LAYOUT_CLASS_NAME} & {
    max-width: 100%;
  }
`
